import React from 'react';
import moment from 'moment';
import { formatCurrency } from '../../../../../../../../../../utils/functions';
import './ShiftsBreakdown.scss';
import OpenerSalesSection, { attributionCopy, OpenerSalesReport } from '../OpenerSalesSection';

interface UserStats {
  name: string;
  total: number;
  count: number;
  avg_total: number;
  tip: number;
  avg_tip: number;
  cancelled: number;
  cancelled_amount: number;
  avg_cancelled: number;
  avg_tip_variation: number | null;
}

interface TypeStats {
  name: string;
  total: number;
  count: number;
  avg_total: number;
}

interface ShiftStatistics {
  opener_sales?: OpenerSalesReport;
  shift_id: number;
  name: string;
  start_time: string;
  end_time: string | null;
  active: boolean;
  open_user_id: number;
  close_user_id: number | null;
  open_user_name: string;
  close_user_name: string | null;
  total_charged: number;
  gross_sales: number;
  valid_receipt_count: number;
  users_count: number;
  net_sales: number;
  pending_payments_count: number;
  total_pending: number;
  gross_loss: number;
  net_loss: number;
  cancelled_receipt_count: number;
  avg_ticket: number;
  total_tip: number;
  avg_tip_by_ticket: number;
  total_discounts: number;
  total_coupons: number;
  avg_tip_by_user: number;
  avg_ticket_pp?: number;
  total_people_served?: number;
  users_stats: { [userId: string]: UserStats };
  type_stats: { [typeId: string]: TypeStats };
}

interface ShiftCardProps {
  shift: ShiftStatistics;
}

const TAX_RATE = 0.19; // 19% IVA for Chile

export const ShiftCard: React.FC<ShiftCardProps> = ({ shift }) => {
  const calculateTaxes = (netSales: number): number => {
    const preTaxAmount = netSales / (1 + TAX_RATE);
    return netSales - preTaxAmount;
  };

  const calculatePreTaxSales = (netSales: number): number => {
    return netSales / (1 + TAX_RATE);
  };

  const shiftPreTaxSales = calculatePreTaxSales(shift.net_sales);
  const shiftTaxes = calculateTaxes(shift.net_sales);
  const shiftTotalWithTip = shift.net_sales + shift.total_tip;

  return (
    <div className="sb-shift-card">
      <div className="sb-shift-header">
        <span className="sb-shift-name">{shift.name}</span>
        <span className="sb-shift-badge">{shift.active ? 'Activo' : 'Cerrado'}</span>
      </div>
      <div className="sb-shift-time">
        {moment(shift.start_time).format('DD/MM/YYYY HH:mm')}
        {shift.end_time && ` - ${moment(shift.end_time).format('DD/MM/YYYY HH:mm')}`}
      </div>
      
      {/* Shift Sales Metrics */}
      <div className="sb-shift-metrics">
        <div className="sb-row">
          <span className="sb-label">Total Venta (con propina)</span>
          <span className="sb-value">{formatCurrency(shiftTotalWithTip)}</span>
        </div>
        <div className="sb-row">
          <span className="sb-label">Total Descuentos</span>
          <span className="sb-value">{formatCurrency(shift.total_discounts)}</span>
        </div>
        <div className="sb-row">
          <span className="sb-label">Venta Bruta</span>
          <span className="sb-value">{formatCurrency(shift.net_sales)}</span>
        </div>
        <div className="sb-row">
          <span className="sb-label">Impuestos</span>
          <span className="sb-value">{formatCurrency(shiftTaxes)}</span>
        </div>
        <div className="sb-row">
          <span className="sb-label">Venta Sin Impuestos</span>
          <span className="sb-value">{formatCurrency(shiftPreTaxSales)}</span>
        </div>
        <div className="sb-row">
          <span className="sb-label">Recibos</span>
          <span className="sb-value">{shift.valid_receipt_count}</span>
        </div>
        <div className="sb-row">
          <span className="sb-label">Ticket Promedio</span>
          <span className="sb-value">{formatCurrency(shift.avg_ticket)}</span>
        </div>
        {shift.avg_ticket_pp != null && shift.avg_ticket_pp > 0 && (
          <div className="sb-row">
            <span className="sb-label">Ticket Prom. por Persona</span>
            <span className="sb-value">{formatCurrency(shift.avg_ticket_pp)}</span>
          </div>
        )}
        {shift.total_people_served != null && shift.total_people_served > 0 && (
          <div className="sb-row">
            <span className="sb-label">Personas Atendidas</span>
            <span className="sb-value">{shift.total_people_served}</span>
          </div>
        )}
        <div className="sb-row">
          <span className="sb-label">Propinas</span>
          <span className="sb-value">{formatCurrency(shift.total_tip)}</span>
        </div>
      </div>

      {shift.opener_sales && <OpenerSalesSection report={shift.opener_sales} />}
      {/* Shift User Stats */}
      {shift.users_stats && Object.keys(shift.users_stats).length > 0 && (
        <div className="sb-shift-subsection">
          <h5 className="sb-subsection-title">{shift.opener_sales
            ? attributionCopy.legacyUsers : 'Usuarios en este Turno'}</h5>
          {Object.values(shift.users_stats).map((user) => (
            <div key={user.name} className="sb-user-mini">
              <div className="sb-row">
                <span className="sb-label">{user.name} ({user.count})</span>
                <span className="sb-value">{formatCurrency(user.total)}</span>
              </div>
              <div className="sb-row">
                <span className="sb-label">Propinas</span>
                <span className="sb-value">{formatCurrency(user.tip)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Shift Type Stats */}
      {shift.type_stats && Object.keys(shift.type_stats).length > 0 && (
        <div className="sb-shift-subsection">
          <h5 className="sb-subsection-title">Tipos en este Turno</h5>
          {Object.values(shift.type_stats).map((type) => (
            <div key={type.name} className="sb-row">
              <span className="sb-label">{type.name} ({type.count})</span>
              <span className="sb-value">{formatCurrency(type.total)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface ShiftsBreakdownProps {
  shifts: { [shiftId: string]: ShiftStatistics };
}

const ShiftsBreakdown: React.FC<ShiftsBreakdownProps> = ({ shifts }) => {
  if (!shifts || Object.keys(shifts).length === 0) {
    return null;
  }

  return (
    <div className="sb-section">
      <h4 className="sb-section-title">Desglose por Turno</h4>
      {Object.values(shifts).map((shift) => (
        <ShiftCard key={shift.shift_id} shift={shift} />
      ))}
    </div>
  );
};

export default ShiftsBreakdown;
